import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';

/* ---- motion helpers ---- */
export const FadeUp = ({ children, delay = 0, className = '' }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.7, delay, ease: [0.21, 0.6, 0.35, 1] }}
  >
    {children}
  </motion.div>
);

export const Stagger = ({ children, className = '' }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: '-60px' }}
    variants={{ show: { transition: { staggerChildren: 0.07 } } }}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = '' }) => (
  <motion.div
    className={className}
    variants={{
      hidden: { opacity: 0, y: 22 },
      show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.21, 0.6, 0.35, 1] } },
    }}
  >
    {children}
  </motion.div>
);

/* ---- eyebrow: quiet uppercase microlabel with a leading tick ---- */
export const Eyebrow = ({ label, dark = false }) => (
  <span
    className={`v2-mono inline-flex items-center gap-2.5 text-[12px] uppercase tracking-[0.14em] ${
      dark ? 'text-[#9db4ff]' : 'text-[#1a46e5]'
    }`}
  >
    <span aria-hidden className={`h-px w-6 ${dark ? 'bg-[#5b7cfa]' : 'bg-[#a8bcf0]'}`} />
    {label}
  </span>
);

/* ---- hairline rule with a mono label — section separator ---- */
export const RuleLabel = ({ label, dark = false }) => (
  <div className="flex items-center gap-4">
    <span className={`v2-mono shrink-0 text-[11.5px] font-medium uppercase tracking-[0.16em] ${dark ? 'text-white/50' : 'text-[#7c89a8]'}`}>
      {label}
    </span>
    <span className={`h-px w-full ${dark ? 'bg-white/15' : 'bg-[#dde5f3]'}`} />
  </div>
);

/* ---- section heading block: serif display ---- */
export const SectionHead = ({ eyebrow, title, body, dark = false, center = false, size = 'md' }) => (
  <FadeUp className={`${center ? 'mx-auto text-center' : ''} max-w-3xl`}>
    {eyebrow && <Eyebrow label={eyebrow} dark={dark} />}
    <h2
      className={`v2-display mt-5 font-semibold leading-[1.06] ${
        size === 'lg' ? 'text-4xl md:text-6xl' : 'text-[34px] md:text-[48px]'
      } ${dark ? 'text-white' : 'text-[#0a1633]'}`}
    >
      {title}
    </h2>
    {body && (
      <p className={`mt-6 text-lg leading-relaxed ${dark ? 'text-white/75' : 'text-[#3f4d6e]'} ${center ? 'mx-auto' : ''} max-w-2xl`}>
        {body}
      </p>
    )}
  </FadeUp>
);

/* ---- CTA buttons ---- */
export const CtaPrimary = ({ to = '/book-demo', children = 'Get a demo', onBlue = false, className = '' }) => (
  <Link
    to={to}
    className={`group inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-[15px] font-bold transition-all duration-200 ${
      onBlue
        ? 'bg-white text-[#1a46e5] hover:-translate-y-0.5 hover:shadow-[0_14px_36px_-10px_rgba(0,0,0,0.4)]'
        : 'bg-[#1a46e5] text-white shadow-[0_10px_28px_-10px_rgba(26,70,229,0.65)] hover:-translate-y-0.5 hover:bg-[#1236bd] hover:shadow-[0_16px_36px_-10px_rgba(26,70,229,0.7)]'
    } ${className}`}
  >
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
  </Link>
);

export const CtaGhost = ({ to, children, onBlue = false, className = '' }) => (
  <Link
    to={to}
    className={`inline-flex items-center gap-2 rounded-full border px-7 py-3.5 text-[15px] font-semibold transition-all duration-200 ${
      onBlue
        ? 'border-white/35 text-white hover:border-white/70 hover:bg-white/10'
        : 'border-[#c4d2ec] bg-white text-[#0a1633] hover:border-[#1a46e5] hover:text-[#1a46e5]'
    } ${className}`}
  >
    {children}
  </Link>
);

/* ---- inline text link with arrow ---- */
export const TextLink = ({ to, children, dark = false, className = '' }) => (
  <Link
    to={to}
    className={`group inline-flex items-center gap-1.5 text-[15px] font-bold transition-colors ${
      dark ? 'text-white hover:text-[#9db4ff]' : 'text-[#1a46e5] hover:text-[#1236bd]'
    } ${className}`}
  >
    {children}
    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </Link>
);

/* ---- app window: clean software chrome with mono title bar ---- */
export const OSWindow = ({ title = 'asha — operating system', children, className = '' }) => (
  <div className={`v2-window overflow-hidden rounded-2xl ${className}`}>
    <div className="flex items-center gap-2 border-b border-[#dde5f3] bg-[#f6f9ff] px-4 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full border border-[#c4d2ec]" />
      <span className="h-2.5 w-2.5 rounded-full border border-[#c4d2ec]" />
      <span className="h-2.5 w-2.5 rounded-full border border-[#c4d2ec]" />
      <span className="v2-mono ml-3 text-[11px] font-medium uppercase tracking-[0.1em] text-[#7c89a8]">{title}</span>
      <span className="v2-mono ml-auto flex items-center gap-1.5 rounded-full bg-[#e6f6f0] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#0f9d76]">
        <span className="v2-pulse h-1.5 w-1.5 rounded-full bg-[#0f9d76]" /> live
      </span>
    </div>
    <div className="relative">{children}</div>
  </div>
);

/* ---- agent avatar with graceful fallback ---- */
export const AgentAvatar = ({ name, img, size = 'h-12 w-12', ring = true }) => {
  const [err, setErr] = React.useState(false);
  return img && !err ? (
    <img
      src={img}
      alt={name}
      onError={() => setErr(true)}
      className={`${size} rounded-full object-cover ${ring ? 'ring-2 ring-[#1a46e5]/15' : ''}`}
    />
  ) : (
    <div className={`${size} flex items-center justify-center rounded-full bg-[#eef3ff] ${ring ? 'ring-2 ring-[#1a46e5]/15' : ''}`}>
      <span className="v2-display text-lg font-semibold text-[#1a46e5]">{name?.[0]}</span>
    </div>
  );
};

/* ---- standard interior-page hero on blueprint grid ---- */
export const PageHero = ({ eyebrow, title, body, children }) => (
  <section className="v2-blueprint-fade relative overflow-hidden px-6 pb-16 pt-40 md:pb-20 md:pt-48">
    <div className="relative mx-auto max-w-7xl">
      <FadeUp className="max-w-4xl">
        {eyebrow && <Eyebrow label={eyebrow} />}
        <h1 className="v2-display mt-6 text-[44px] font-semibold leading-[1.04] text-[#0a1633] md:text-[72px]">
          {title}
        </h1>
        {body && <p className="mt-7 max-w-2xl text-lg leading-relaxed text-[#3f4d6e] md:text-xl">{body}</p>}
        {children && <div className="mt-10 flex flex-wrap items-center gap-4">{children}</div>}
      </FadeUp>
    </div>
  </section>
);

/* ---- big serif stat: number + caption (counts only, no perf claims) ---- */
export const BigStat = ({ value, label, dark = false }) => (
  <div>
    <div className={`v2-display text-5xl font-semibold md:text-7xl ${dark ? 'text-white' : 'text-[#0a1633]'}`}>
      {value}
    </div>
    <div className={`v2-mono mt-3 text-[12px] font-medium uppercase tracking-[0.14em] ${dark ? 'text-[#9db4ff]' : 'text-[#1a46e5]'}`}>
      {label}
    </div>
  </div>
);

/* ---- check bullet ---- */
export const Check = ({ children, dark = false }) => (
  <li className="flex items-start gap-3">
    <svg
      viewBox="0 0 16 16"
      className={`mt-1 h-4 w-4 shrink-0 ${dark ? 'text-[#9db4ff]' : 'text-[#1a46e5]'}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2.5 8.5l3.5 3.5L13.5 4" />
    </svg>
    <span className={`text-[15px] leading-relaxed ${dark ? 'text-white/80' : 'text-[#3f4d6e]'}`}>{children}</span>
  </li>
);

/* ---- ticker marquee of mono items ---- */
export const Marquee = ({ items, fast = false, dark = false }) => (
  <div className={`relative overflow-hidden border-y py-4 ${dark ? 'border-white/15' : 'border-[#dde5f3]'}`}>
    <div className={`${fast ? 'v2-marquee-fast' : 'v2-marquee'} flex w-max items-center gap-10 whitespace-nowrap`}>
      {[...items, ...items].map((item, i) => (
        <span
          key={i}
          className={`v2-mono flex items-center gap-10 text-[12.5px] font-medium uppercase tracking-[0.14em] ${
            dark ? 'text-white/60' : 'text-[#7c89a8]'
          }`}
        >
          {item}
          <span aria-hidden className={dark ? 'text-[#5b7cfa]' : 'text-[#a8bcf0]'}>+</span>
        </span>
      ))}
    </div>
  </div>
);

/* ---- big closing CTA: bright blue blueprint panel ---- */
export const ClosingCta = ({
  title = 'See the operating system running on your workflows.',
  body = 'A 30-minute walkthrough tailored to your practice — watch every agent work a real schedule, real claims, real calls.',
}) => (
  <section className="px-6 pb-24 pt-10 md:pb-32">
    <div className="mx-auto max-w-7xl">
      <FadeUp>
        <div className="v2-panel-bright relative overflow-hidden rounded-[2rem] px-8 py-20 text-center md:px-16 md:py-28">
          <span className="v2-mono absolute left-6 top-5 hidden text-[11px] font-medium uppercase tracking-[0.16em] text-white/40 md:block">
            asha / demo
          </span>
          <span className="v2-mono absolute right-6 top-5 hidden text-[11px] font-medium uppercase tracking-[0.16em] text-white/40 md:block">
            30 min — tailored
          </span>
          <h2 className="v2-display mx-auto max-w-3xl text-4xl font-semibold leading-[1.07] text-white md:text-6xl">
            {title}
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-white/80">{body}</p>
          <div className="mt-11 flex flex-wrap items-center justify-center gap-4">
            <CtaPrimary onBlue />
            <CtaGhost to="/contact" onBlue>Talk to our team</CtaGhost>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);
